/* =============================================================================
 * Arachne Audio - unified cart grouping on the /cart PAGE
 *
 * Companion to assets/cart-drawer-groups.js. The controller in that file is hard-
 * wired to <cart-drawer>; this small controller does the SAME job for the stock
 * Dawn /cart PAGE (sections/main-cart-items.liquid). It REUSES the shared grouping
 * model + DOM card renderers exported on window.AcfgCartGroups, so the drawer and
 * the page can never diverge (no grouping/render logic is duplicated here).
 *
 * It reads the page data island (#MainCart-AcfgData), folds a configured cable
 * (base line + hidden add-on lines sharing _config_id) into ONE card with the
 * summed price, atomic remove (every line at once) + 6s undo, atomic group
 * quantity, and Edit / Keep configuring. Plain products render as ordinary single
 * rows in the same list. Edit reuses the SAME cross-page handoff as the drawer
 * (sessionStorage 'acfg_prefill' + navigate to the product), so the configurator
 * engine pre-fills identically.
 *
 * Self-contained: it runs its own /cart AJAX + Section Rendering refetch and does
 * NOT depend on Dawn's cart.js (which this drawer-mode store does not even load on
 * the cart page). FAIL-SAFE CONTRACT: every enhancement path is wrapped in
 * try/catch; on ANY error the stock Dawn cart table is left fully intact and
 * visible (it is only hidden once the grouped view is built successfully).
 * ========================================================================== */
(function () {
  "use strict";

  if (typeof document === "undefined" || !document.querySelector) return;
  // Idempotent: a second include on the same page must not re-bind listeners.
  if (window.__acfgCartPageBound) return;
  window.__acfgCartPageBound = true;

  var API = window.AcfgCartGroups;
  // Shared module missing/too old -> do nothing; the stock Dawn cart stays intact.
  if (!API || typeof API.groupLines !== "function" || typeof API.renderView !== "function") return;

  var UNDO_MS = 6000;

  var observer = null;
  var enhanceQueued = false;
  var busy = false;
  var currentView = [];
  var currentFormat = "";
  var undoState = null; // { items, model, timer }

  /* ------------------------------ tiny helpers ---------------------------- */
  function el(tag, cls, html) {
    if (API.el) return API.el(tag, cls, html);
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function txt(s) { return document.createTextNode(s == null ? "" : String(s)); }
  function disp(s) { return API.displayValue ? API.displayValue(s) : String(s == null ? "" : s); }
  /* Localized UI strings: same JSON island contract as cart-drawer-groups.js
   * (<script type="application/json" data-aa-cart-strings>, emitted by the
   * Liquid before these scripts load, translated via locales cart_groups.*).
   * Read once, lazily; hard fallback to the previous English literals so a
   * missing/broken island can never break rendering. Mini-interpolator fills
   * %{model}/%{title}. */
  var AA_STRING_DEFAULTS = {
    edit: "Edit",
    build_another: "Build another",
    remove: "Remove",
    undo: "Undo",
    removed_model: "Removed %{model}.",
    qty_decrease: "Decrease quantity",
    qty_increase: "Increase quantity",
    remove_title: "Remove %{title}"
  };
  var aaStrings = null;
  function aaT(key, vars) {
    if (!aaStrings) {
      aaStrings = {};
      try {
        var node = document.querySelector("script[data-aa-cart-strings]");
        if (node) aaStrings = JSON.parse(node.textContent || "{}") || {};
      } catch (e) { aaStrings = {}; }
    }
    var s = aaStrings[key];
    if (typeof s !== "string" || !s) s = AA_STRING_DEFAULTS[key] || "";
    if (vars) {
      for (var k in vars) {
        if (Object.prototype.hasOwnProperty.call(vars, k)) {
          var v = vars[k] == null ? "" : String(vars[k]);
          // Two token forms: %{k} (locale convention / fallback defaults) and @@K@@
          // (the Liquid island's brace-free sentinel — literal %{...} braces break
          // Shopify's tag parser).
          s = s.split("@@" + k.toUpperCase() + "@@").join(v);
          s = s.split("%{" + k + "}").join(v);
        }
      }
    }
    return s;
  }
  function routeUrl(key, fallback) { return (window.routes && window.routes[key]) || fallback; }
  function sectionIdOf(domId, fallback) {
    var node = document.getElementById(domId);
    return (node && node.dataset && node.dataset.id) || fallback;
  }

  function announce(message) {
    var live = document.getElementById("cart-live-region-text");
    if (live) { live.setAttribute("aria-hidden", "false"); live.textContent = message; }
  }
  function showError(message) {
    var box = document.getElementById("cart-errors") || document.getElementById("CartDrawer-CartErrors");
    if (box) box.textContent = message || "";
    else if (message && window.console && console.warn) console.warn("[acfg-cart-page] " + message);
  }

  /* ----------------------------- enhancement ------------------------------ */
  function scheduleEnhance() {
    if (enhanceQueued) return;
    enhanceQueued = true;
    window.setTimeout(function () { enhanceQueued = false; enhance(); }, 0);
  }

  function enhance() {
    var cartItems = document.querySelector("cart-items");
    if (!cartItems) return;
    var dataNode = document.getElementById("MainCart-AcfgData");
    var host = document.getElementById("MainCart-AcfgGroups");
    // Empty cart, or island/host not present -> leave the stock markup exactly as
    // Dawn rendered it (Dawn's own empty state shows via the is-empty class).
    if (!dataNode || !host) { cartItems.classList.remove("aa-cart-enhanced"); return; }

    if (observer) observer.disconnect();
    try {
      var data = JSON.parse(dataNode.textContent || "{}");
      currentFormat = data.money_format || "";
      currentView = API.groupLines(data.lines || []);
      var frag = API.renderView(currentView, currentFormat);
      host.innerHTML = "";
      host.appendChild(frag);
      host.removeAttribute("hidden");
      cartItems.classList.add("aa-cart-enhanced");
      showError("");
    } catch (e) {
      // Fail-safe: revert to the stock flat table, never break the cart page.
      try {
        cartItems.classList.remove("aa-cart-enhanced");
        host.innerHTML = "";
        host.setAttribute("hidden", "");
      } catch (e2) {}
      if (window.console && console.error) console.error("[acfg-cart-page] grouping failed; stock cart shown", e);
    } finally {
      reconnect();
    }
  }

  // Observe the STABLE <cart-items> element (Dawn swaps its inner HTML, never the
  // element itself), so an external section refetch re-triggers enhancement.
  function reconnect() {
    var target = document.querySelector("cart-items");
    if (!target) return;
    if (!observer) observer = new MutationObserver(function () { scheduleEnhance(); });
    try { observer.observe(target, { childList: true, subtree: true }); } catch (e) {}
  }

  /* ------------------------- cart mutations (AJAX) ------------------------ */
  function requestedSections() {
    var arr = [sectionIdOf("main-cart-items", "main-cart-items")];
    var footer = sectionIdOf("main-cart-footer", null);
    if (footer) arr.push(footer);
    arr.push("cart-icon-bubble");
    return arr;
  }

  function postCart(url, payload) {
    payload.sections = requestedSections();
    payload.sections_url = window.location.pathname;
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw new Error((body && (body.description || body.message)) || "Cart update failed.");
        return body;
      });
    });
  }

  function sectionInner(html, selector) {
    try {
      var doc = new DOMParser().parseFromString(html, "text/html");
      var node = doc.querySelector(selector);
      return node ? node.innerHTML : null;
    } catch (e) { return null; }
  }
  function swapJsContents(containerId, html) {
    if (!html) return;
    var container = document.getElementById(containerId);
    if (!container) return;
    var dst = container.querySelector(".js-contents");
    var inner = sectionInner(html, ".js-contents");
    if (dst && inner != null) dst.innerHTML = inner;
  }
  function updateBubble(html) {
    if (!html) return;
    var bubble = document.getElementById("cart-icon-bubble");
    if (!bubble) return;
    var target = bubble.querySelector(".shopify-section") || bubble;
    var inner = sectionInner(html, ".shopify-section");
    if (inner != null) target.innerHTML = inner;
  }
  // This store's custom header shows the count in [data-cart-count], not a
  // #cart-icon-bubble section, so keep it live too (mirrors the header's format).
  function updateCartCount(count) {
    if (typeof count !== "number") return;
    var label = count > 99 ? "99+" : (count > 0 ? String(count) : "");
    var nodes = document.querySelectorAll("[data-cart-count]");
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = label;
  }

  function applySections(state) {
    if (!state) return;
    if (state.sections) {
      swapJsContents("main-cart-items", state.sections[sectionIdOf("main-cart-items", "main-cart-items")]);
      swapJsContents("main-cart-footer", state.sections[sectionIdOf("main-cart-footer", "main-cart-footer")]);
      updateBubble(state.sections["cart-icon-bubble"]);
    }
    if (typeof state.item_count === "number") {
      var cartItems = document.querySelector("cart-items");
      var footer = document.getElementById("main-cart-footer");
      if (cartItems) cartItems.classList.toggle("is-empty", state.item_count === 0);
      if (footer) footer.classList.toggle("is-empty", state.item_count === 0);
      updateCartCount(state.item_count);
    }
    // Let the rest of the theme sync. Source "cart-items" matches the drawer
    // controller and stops Dawn's cart-items / drawer-items subscribers from
    // firing a second, redundant section refetch (they ignore that source).
    try {
      if (typeof publish === "function" && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items", cartData: state });
      }
    } catch (e) {}
  }

  function updateCart(updates) {
    if (busy) return Promise.resolve(null);
    busy = true;
    var host = document.getElementById("MainCart-AcfgGroups");
    if (host) host.classList.add("aa-busy");
    return postCart(routeUrl("cart_update_url", "/cart/update.js"), { updates: updates })
      .then(function (state) { applySections(state); scheduleEnhance(); return state; })
      .catch(function (err) { showError(err.message || "Could not update cart."); return null; })
      .then(function (state) {
        busy = false;
        if (host) host.classList.remove("aa-busy");
        return state;
      });
  }

  /* -------------------------------- undo ---------------------------------- */
  function clearUndo() {
    if (undoState && undoState.timer) window.clearTimeout(undoState.timer);
    undoState = null;
    var t = document.getElementById("MainCart-AcfgToast");
    if (t && t.parentNode) t.parentNode.removeChild(t);
  }
  function showUndo(model, items) {
    clearUndo();
    var toast = el("div", "aa-toast aa-toast--page");
    toast.id = "MainCart-AcfgToast";
    toast.setAttribute("role", "status");
    var msg = el("span", "aa-toast__msg");
    msg.appendChild(txt(aaT("removed_model", { model: model || "item" })));
    var undoBtn = el("button", "aa-toast__undo");
    undoBtn.type = "button";
    undoBtn.setAttribute("data-aa-action", "undo");
    undoBtn.appendChild(txt(aaT("undo")));
    toast.appendChild(msg);
    toast.appendChild(undoBtn);
    document.body.appendChild(toast);
    undoState = { items: items, model: model, timer: window.setTimeout(clearUndo, UNDO_MS) };
    try { undoBtn.focus(); } catch (e) {}
  }
  function doUndo() {
    if (!undoState || busy) return;
    var items = undoState.items;
    clearUndo();
    busy = true;
    var host = document.getElementById("MainCart-AcfgGroups");
    if (host) host.classList.add("aa-busy");
    postCart(routeUrl("cart_add_url", "/cart/add.js"), { items: items })
      .then(function (state) { applySections(state); scheduleEnhance(); announce("Item restored to cart."); })
      .catch(function (err) { showError(err.message || "Could not restore the item."); })
      .then(function () { busy = false; if (host) host.classList.remove("aa-busy"); });
  }

  /* -------------------------- edit / keep configuring --------------------- */
  // Cross-page handoff identical to the drawer: stash the visible option map under
  // the SAME sessionStorage key and navigate to the product page, where the engine
  // consumes it on init (and only applies it when _model matches the product).
  function stashPrefill(group) {
    var model = group.model || "";
    var props = (group.base && group.base.properties) || {};
    var map = {};
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i++) { if (keys[i].charAt(0) !== "_") map[keys[i]] = props[keys[i]]; }
    // The edited group's line keys ride along so the engine can REPLACE the old
    // lines when (and only when) the re-configured cable is successfully added.
    var lineKeys = [];
    for (var lk = 0; lk < group.lines.length; lk++) { if (group.lines[lk] && group.lines[lk].key) lineKeys.push(group.lines[lk].key); }
    try { window.sessionStorage.setItem("acfg_prefill", JSON.stringify({ model: model, props: map, replace_keys: lineKeys })); } catch (e) {}
  }
  function editGroup(group) {
    stashPrefill(group);
    var url = group.base && group.base.url;
    if (url) window.location.href = url;
  }
  // "Build another": open a fresh configurator for this model (no prefill).
  function keepConfiguring(group) {
    var url = group.base && group.base.url;
    if (url) window.location.href = url;
  }

  /* ------------------------------ interactions ---------------------------- */
  function findGroup(cid) {
    for (var i = 0; i < currentView.length; i++) {
      if (currentView[i].type === "group" && currentView[i].configId === cid) return currentView[i];
    }
    return null;
  }
  function findLine(key) {
    for (var i = 0; i < currentView.length; i++) {
      if (currentView[i].type === "product" && currentView[i].line.key === key) return currentView[i].line;
    }
    return null;
  }

  function onClick(e) {
    var btn = e.target && e.target.closest ? e.target.closest("[data-aa-action]") : null;
    if (!btn) return;
    // Only handle actions inside the PAGE's own host/toast. The drawer controller
    // owns #CartDrawer-* and is scoped the same way, so the two never collide even
    // though both bind a single document-level click listener.
    if (!btn.closest("#MainCart-AcfgGroups") && !btn.closest("#MainCart-AcfgToast")) return;

    var action = btn.getAttribute("data-aa-action");
    if (action === "undo") { e.preventDefault(); doUndo(); return; }

    var cid = btn.getAttribute("data-aa-cid");
    var key = btn.getAttribute("data-aa-key");
    var group = cid ? findGroup(cid) : null;
    var line = key ? findLine(key) : null;

    if (action === "remove-group" && group) {
      e.preventDefault();
      var items = API.buildUndoItems(group);
      var model = group.model;
      // The undo toast (role="status") announces the removal; no double-announce.
      updateCart(API.buildRemoveUpdates(group)).then(function (state) { if (state) showUndo(model, items); });
    } else if (action === "group-inc" && group) {
      e.preventDefault();
      updateCart(API.buildSetQtyUpdates(group, (group.quantity || 1) + 1));
    } else if (action === "group-dec" && group) {
      e.preventDefault();
      var nq = (group.quantity || 1) - 1;
      if (nq >= 1) updateCart(API.buildSetQtyUpdates(group, nq));
    } else if (action === "edit" && group) {
      e.preventDefault();
      editGroup(group);
    } else if (action === "keep" && group) {
      e.preventDefault();
      keepConfiguring(group);
    } else if (action === "remove-line" && line) {
      e.preventDefault();
      var lineItems = [{ id: line.variant_id, quantity: line.quantity, properties: line.properties || {} }];
      var lineTitle = disp(line.title);
      // The undo toast (role="status") announces the removal; no double-announce.
      var u = {}; u[line.key] = 0;
      updateCart(u).then(function (state) { if (state) showUndo(lineTitle, lineItems); });
    } else if (action === "line-inc" && line) {
      e.preventDefault();
      var lu = {}; lu[line.key] = (line.quantity || 1) + 1;
      updateCart(lu);
    } else if (action === "line-dec" && line) {
      e.preventDefault();
      var ld = (line.quantity || 1) - 1;
      if (ld >= 1) { var d = {}; d[line.key] = ld; updateCart(d); }
    }
  }

  /* --------------------------------- init --------------------------------- */
  function init() {
    if (!document.querySelector("cart-items")) return; // not the /cart page
    document.addEventListener("click", onClick);
    reconnect();
    enhance();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();

